import { shrpaApi } from "./axios";

// Types
import { CustomCollabClientPayload, CustomCollabCustomerData, CustomCollabCustomerTemplate, CustomCollabWidgetPayload, NewCustomCollabPayload } from "@contracts/customCollabs";

export default {
  async getAllCustomCollabsV2(customerId: string, impersonatedUserCreatorId: string | null = null): Promise<Array<CustomCollabWidgetPayload>> {
    // Add impersonatedUserCreatorId to query params if it exists
    const queryParams: Record<string, string> = {};
    if (impersonatedUserCreatorId) {
      queryParams.impersonatedUserCreatorId = impersonatedUserCreatorId;
    }

    const queryString = Object.keys(queryParams).length > 0 ? `?${new URLSearchParams(queryParams).toString()}` : "";
    const uri = `/customers/${customerId}/custom-collabs/v2${queryString}`;

    const { data } = await shrpaApi.get<Array<any>>(uri);

    return data;
  },

  async getCustomCollab(customerId: string, customCollabId: string): Promise<CustomCollabClientPayload> {
    const uri = `/customers/${customerId}/custom-collabs/${customCollabId}`;
    const { data } = await shrpaApi.get<CustomCollabClientPayload>(uri);

    return data;
  },

  async saveCustomCollab(customerId: string, customCollabId: string, customerCollabInputs: CustomCollabCustomerData, isImpersonating = false): Promise<CustomCollabClientPayload> {
    // If impersonating, add the "impersonate" to the querystring
    const queryParams = isImpersonating ? { impersonate: "true" } : {};
    const uri = `/customers/${customerId}/custom-collabs/${customCollabId}?${new URLSearchParams(queryParams).toString()}`;
    const config = { headers: { "Content-Type": "application/json" } };
    const payload = JSON.stringify(customerCollabInputs);

    const { data } = await shrpaApi.put<CustomCollabClientPayload>(uri, payload, config);

    return data;
  },

  async formalizeCustomCollab(customerId: string, customCollabId: string): Promise<CustomCollabClientPayload> {
    const uri = `/customers/${customerId}/custom-collabs/${customCollabId}/formalize`;
    const { data } = await shrpaApi.post<CustomCollabClientPayload>(uri);

    return data;
  },

  async markCustomCollabComplete(customerId: string, customCollabId: string): Promise<CustomCollabClientPayload> {
    const uri = `/customers/${customerId}/custom-collabs/${customCollabId}/mark-complete`;
    const { data } = await shrpaApi.post<CustomCollabClientPayload>(uri);

    return data;
  },

  async getTemplates(customerId: string): Promise<Array<CustomCollabCustomerTemplate>> {
    const uri = `/customers/${customerId}/custom-collabs/templates`;
    const { data } = await shrpaApi.get<Array<CustomCollabCustomerTemplate>>(uri);
    return data;
  },

  async improveInitialMsgWithAI(customerId: string, message: string, guidance: string): Promise<string> {
    const uri = `/customers/${customerId}/custom-collabs/ai/initial-message`;
    const config = { headers: { "Content-Type": "application/json" } };
    const payload = JSON.stringify({ message, guidance });

    const { data } = await shrpaApi.put<string>(uri, payload, config);

    return data;
  },

  async fillCustomerFieldsWithAI(customerId: string, customCollabId: string): Promise<CustomCollabCustomerData> {
    const saveMode = "SaveOverEmpty"; // Could give the user a choice
    const uri = `/customers/${customerId}/custom-collabs/ai/formalize-fields/${customCollabId}?saveMode=${saveMode}`;
    const { data } = await shrpaApi.put<CustomCollabCustomerData>(uri);
    return data;
  },

  async createCustomCollab(customerId: string, newCustomCollabPayload: NewCustomCollabPayload): Promise<CustomCollabCustomerData> {
    const uri = `/customers/${customerId}/custom-collabs`;
    const config = { headers: { "Content-Type": "application/json" } };
    const payload = JSON.stringify(newCustomCollabPayload);

    const { data } = await shrpaApi.post<CustomCollabCustomerData>(uri, payload, config);

    return data;
  },

  async deleteCustomCollab(customerId: string, customCollabId: string): Promise<void> {
    const uri = `/customers/${customerId}/custom-collabs/${customCollabId}`;
    await shrpaApi.delete(uri);
  },

  async createCustomCollabFromCreatorPackage(creatorId: string, customerId: string, packageId: string): Promise<CustomCollabCustomerData> {
    const uri = `/customers/${customerId}/custom-collabs/creator/${creatorId}/packages/${packageId}`;
    const { data } = await shrpaApi.post<CustomCollabCustomerData>(uri);
    return data;
  },
};
